import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../authentication/AuthenticationContext';
import { PromotionData } from '../Promotion.model';
import { ProviderComponent } from './Provider';

interface Props {
  promotionData: PromotionData;
  selectProvider: (provider: string) => void;
}

export function RewardProviderSelection({ promotionData, selectProvider }: Props): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [provider, setProvider] = useState<string>(null);

  useEffect(() => {
    if (user && promotionData.redeemableOnPlatformIds.length === 1) {
      selectProvider(promotionData.redeemableOnPlatformIds[0]);
    }
  }, [promotionData]);

  useEffect(() => {
    if (user.accounts.some((account) => account.type === provider)) {
      selectProvider(provider);
    }
  }, [provider]);

  const resetProvider = (): void => {
    setProvider(null);
  };

  const onSelect = (selectedProvider: string): void => {
    setProvider(selectedProvider);
  };

  return (
    <>
      {provider && (
        <>
          <p className='claim-promotion-instructions'>
            {t('promotion.reward-provider.not-linked-description')}
          </p>
          <ProviderComponent name={provider} />
          <div className='mt-4'>
            <button
              type='button'
              className='btn btn-link'
              data-cy='select-provider-cancel-btn'
              onClick={resetProvider}
            >
              {t('common.cancel')}
            </button>
          </div>
        </>
      )}

      {!provider && (
        <>
          <p className='claim-promotion-instructions'>
            {t(
              'promotion.reward-provider.provider-selection-description',
              { rewardGame: t(`games.${promotionData.relatedToGameId}`) },
            )}
            <br />
            <span className='subtitle'>
              {t(
                'promotion.reward-provider.provider-selection-note',
                { rewardGame: t(`games.${promotionData.relatedToGameId}`) },
              )}
            </span>
          </p>
          <div className='providers-container'>
            {promotionData.redeemableOnPlatformIds.length > 1
              && promotionData.redeemableOnPlatformIds.map((platform) => (
                <div key={platform} className='provider-selection'>
                  <ProviderComponent name={platform} showLink={false} showUnlink={false} />
                  <button
                    type='submit'
                    data-cy='select-provider-btn'
                    className='btn btn-primary-game'
                    onClick={() => onSelect(platform)}
                  >
                    {t('button.select')}
                  </button>
                </div>
              ))}
          </div>
        </>
      )}
    </>
  );
}
